import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, UploadCloud, FileText, CheckCircle2, AlertTriangle, Loader2, ShieldCheck, Scale } from "lucide-react";

const DOC_SLOTS = [
  { role: "policy", title: "Policy Copy", hint: "Defines coverage & clauses", required: true },
  { role: "insurer_letter", title: "Rejection / Short-settlement Letter", hint: "The insurer's decision", required: true },
  { role: "claim_file", title: "Claim File", hint: "Documents submitted to insurer", required: false },
];

function UploadSlot({ slot, caseId, onUploaded, doc }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_role", slot.role);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/cases/${caseId}/documents`, fd);
      onUploaded(slot.role, data);
      if (data.extraction_warning) toast.warning(data.extraction_warning);
      else toast.success(`${file.name} uploaded · ${data.chars} characters read`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-md border-border p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-medium text-primary">
            <FileText className="h-4 w-4 text-accent" /> {slot.title}
            {slot.required && <span className="text-xs text-accent">*</span>}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{slot.hint}</p>
        </div>
        {doc && <CheckCircle2 className="h-5 w-5 text-success" />}
      </div>

      {doc ? (
        <div className="mt-3 rounded-md bg-secondary p-3 text-xs">
          <div className="font-medium text-primary">{doc.original_filename}</div>
          <div className="mt-1 flex items-center gap-2 text-muted-foreground">
            {doc.extraction_warning ? (
              <span className="flex items-center gap-1 text-warning"><AlertTriangle className="h-3 w-3" />{doc.extraction_warning}</span>
            ) : (
              <span>{doc.chars} characters extracted · {doc.extraction_method}</span>
            )}
          </div>
        </div>
      ) : (
        <label className="mt-3 flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border bg-background p-6 text-center transition-colors duration-200 hover:border-accent/50">
          {busy ? <Loader2 className="h-6 w-6 animate-spin text-accent" /> : <UploadCloud className="h-6 w-6 text-muted-foreground" />}
          <span className="text-xs text-muted-foreground">{busy ? "Reading document…" : "PDF, DOCX, image or text"}</span>
          <input type="file" className="hidden" accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp" onChange={handle} disabled={busy}
            data-testid={`upload-${slot.role}-input`} />
        </label>
      )}
    </Card>
  );
}

export default function NewCase() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [caseId, setCaseId] = useState(null);
  const [form, setForm] = useState({ title: "", insurer_name: "", claimant_name: "", policy_number: "", claim_number: "", notes: "" });
  const [docs, setDocs] = useState({});

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const createCase = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/cases", form);
      setCaseId(data.id);
      setStep(2);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/cases/${caseId}/analyze`);
      toast.success("Analysis complete");
      navigate(`/cases/${caseId}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setAnalyzing(false);
    }
  };

  const hasRequired = DOC_SLOTS.filter((s) => s.required).every((s) => docs[s.role]);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl">
        <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="newcase-back-btn">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </button>

        <div className="mt-4 flex items-center gap-3">
          {[1, 2].map((n) => (
            <div key={n} className="flex items-center gap-3">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${step >= n ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>{n}</div>
              <span className={`text-sm font-medium ${step >= n ? "text-primary" : "text-muted-foreground"}`}>{n === 1 ? "Case Details" : "Upload Documents"}</span>
              {n === 1 && <div className="h-px w-8 bg-border" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={createCase} className="mt-8 space-y-5" data-testid="case-details-form">
            <h1 className="font-heading text-3xl font-bold text-primary">New Case</h1>
            <div className="space-y-2">
              <Label htmlFor="title">Case title *</Label>
              <Input id="title" required data-testid="case-title-input" placeholder="e.g. Mediclaim rejection — Apollo hospitalization" value={form.title} onChange={upd("title")} />
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-2"><Label htmlFor="insurer">Insurer</Label><Input id="insurer" data-testid="case-insurer-input" placeholder="e.g. Star Health" value={form.insurer_name} onChange={upd("insurer_name")} /></div>
              <div className="space-y-2"><Label htmlFor="claimant">Claimant / Insured</Label><Input id="claimant" data-testid="case-claimant-input" placeholder="Full name" value={form.claimant_name} onChange={upd("claimant_name")} /></div>
              <div className="space-y-2"><Label htmlFor="policy">Policy number</Label><Input id="policy" data-testid="case-policy-input" value={form.policy_number} onChange={upd("policy_number")} /></div>
              <div className="space-y-2"><Label htmlFor="claim">Claim number</Label><Input id="claim" data-testid="case-claim-input" value={form.claim_number} onChange={upd("claim_number")} /></div>
            </div>
            <div className="space-y-2"><Label htmlFor="notes">Notes for reviewer (optional)</Label><Textarea id="notes" data-testid="case-notes-input" rows={3} placeholder="Any context that helps the analysis" value={form.notes} onChange={upd("notes")} /></div>
            <Button type="submit" disabled={saving} data-testid="create-case-btn" className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Continue to upload
            </Button>
          </form>
        )}

        {step === 2 && (
          <div className="mt-8 space-y-5" data-testid="upload-step">
            <div>
              <h1 className="font-heading text-3xl font-bold text-primary">Upload Documents</h1>
              <p className="mt-2 text-sm text-muted-foreground">We extract text and analyze only what's in your documents. Nothing is invented.</p>
            </div>

            <Card className="flex items-start gap-3 rounded-md border-accent/20 bg-accent/5 p-4">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <p className="text-xs text-primary/80">Your files are stored securely and used only to draft your letter. The draft is always marked for human review before issue.</p>
            </Card>

            <div className="grid gap-4">
              {DOC_SLOTS.map((slot) => (
                <UploadSlot key={slot.role} slot={slot} caseId={caseId} doc={docs[slot.role]}
                  onUploaded={(role, data) => setDocs((d) => ({ ...d, [role]: data }))} />
              ))}
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => navigate(`/cases/${caseId}`)} data-testid="skip-analysis-btn">Save & review later</Button>
              <Button onClick={analyze} disabled={!hasRequired || analyzing} data-testid="analyze-btn" className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />}
                {analyzing ? "Analyzing documents…" : "Analyze & continue"}
              </Button>
            </div>
            {!hasRequired && <p className="text-right text-xs text-muted-foreground">Upload the policy copy and insurer's letter to analyze.</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
